import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/hooks/use-toast";
import { cancelBooking, rescheduleBooking } from "@/lib/booking-actions";
import { BookingCancelledDialog } from "../dialogs/BookingCancelledDialog";
import { useBookingContext } from "@/contexts/BookingContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowLeft } from "lucide-react";

interface BookingManagementActionsProps {
  className?: string;
  onBackToDateSelection?: () => void;
}

export const BookingManagementActions: React.FC<BookingManagementActionsProps> = ({
  className = "",
  onBackToDateSelection
}) => {
  const navigate = useNavigate();
  const { existingBooking, isRescheduleMode, isCancelMode, selectedDate, selectedTime } = useBookingContext();
  const [reason, setReason] = useState("");
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showRescheduleSuccess, setShowRescheduleSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  // Check if selected date/time is different from current booking
  const hasDateTimeChanged = useMemo(() => {
    if (!existingBooking || !selectedDate || !selectedTime) return false;
    
    const currentBookingDate = new Date(existingBooking.booking_date);
    const currentDate = currentBookingDate.toDateString();
    const currentTime = currentBookingDate.toLocaleTimeString('en-US', { 
      hour: '2-digit',
      minute: '2-digit',
      hour12: false 
    });
    
    const selectedDateString = selectedDate.toDateString();
    
    return currentDate !== selectedDateString || currentTime !== selectedTime;
  }, [existingBooking, selectedDate, selectedTime]);

  // Only show this component in reschedule or cancel mode
  if (!existingBooking || (!isRescheduleMode && !isCancelMode)) {
    return null;
  }

  const handleCancel = async () => {
    if (!existingBooking) return;

    setLoading(true);
    const result = await cancelBooking({
      bookingId: existingBooking.id,
      reason,
      meetingLinkId: existingBooking.meeting_link_id
    });

    setLoading(false);
    if (result.success) {
      setShowConfirmation(true);
      
      // Check if booking was already cancelled
      if (result.alreadyCancelled) {
        console.log("Booking was already cancelled");
        toast({
          title: "Already Cancelled",
          description: "This meeting was already cancelled.",
        });
      } else {
        console.log("Meeting successfully cancelled and calendar event deleted.");
      }
    } else {
      let errorMessage = result.error || "Failed to cancel the meeting. Please try again.";
      
      // If calendar deletion failed, the entire operation failed to maintain synchronization
      if (result.error?.includes("Calendar deletion failed")) {
        toast({
          title: "Cancellation Failed",
          description: "Could not cancel the calendar event. The meeting remains active to maintain synchronization.",
          variant: "destructive"
        });
      } else {
        toast({
          title: "Error",
          description: errorMessage,
          variant: "destructive"
        });
        
        if (errorMessage.includes("does not exist")) {
          setTimeout(() => navigate("/"), 3000);
        }
      }
    }
  };

  const handleReschedule = async () => {
    if (!existingBooking || !selectedDate || !selectedTime) return;

    // Check if the selected date/time is different from the original booking
    const originalDate = new Date(existingBooking.booking_date);
    const originalTime = originalDate.toLocaleTimeString('en-US', { 
      hour: '2-digit',
      minute: '2-digit',
      hour12: false 
    });
    
    const isSameDate = selectedDate.toDateString() === originalDate.toDateString();
    const isSameTime = selectedTime === originalTime;
    
    if (isSameDate && isSameTime) {
      toast({
        title: "No Changes",
        description: "Please select a different date and time to reschedule your meeting.",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    
    // Combine selected date and time into a new datetime
    const [timeStr] = selectedTime.split(' '); // Remove AM/PM if present and use 24h format
    const newDateTime = new Date(selectedDate);
    const [hours, minutes] = timeStr.split(':');
    newDateTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);

    const result = await rescheduleBooking({
      bookingId: existingBooking.id,
      newDate: newDateTime,
      reason,
      meetingLinkId: existingBooking.meeting_link_id
    });

    setLoading(false);
    if (result.success) {
      setShowRescheduleSuccess(true);
    } else {
      toast({
        title: "Error",
        description: result.error || "Failed to reschedule the meeting. Please try again.",
        variant: "destructive"
      });
      
      if (result.error && result.error.includes("does not exist")) {
        setTimeout(() => navigate("/"), 3000);
      }
    }
  };

  const handleConfirmationClose = () => {
    setShowConfirmation(false);
    navigate("/");
  };

  const handleRescheduleSuccessClose = () => {
    setShowRescheduleSuccess(false);
    navigate("/");
  };

  // Parse booking date for display
  const bookingDate = new Date(existingBooking.booking_date);
  const dateString = bookingDate.toLocaleDateString('en-US', { 
    weekday: 'long',
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });
  const timeString = bookingDate.toLocaleTimeString('en-US', { 
    hour: '2-digit', 
    minute: '2-digit',
    hour12: true 
  });

  return (
    <>
      <Card className={`w-full ${className} shadow-none border-0`}>
        <CardContent className="py-2 space-y-4">
          {/* Current Booking Info */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-medium text-gray-900 mb-2">Current Booking Details</h3>
            <div className="space-y-1 text-sm text-gray-600">
              <p><strong>Meeting:</strong> {existingBooking.meeting_name}</p>
              <p><strong>Date:</strong> {dateString}</p>
              <p><strong>Time:</strong> {timeString} ({existingBooking.timezone})</p>
              <p><strong>Duration:</strong> {existingBooking.duration} minutes</p>
              <p><strong>Status:</strong> {existingBooking.status}</p>
            </div>
          </div>

          {/* Actions */}
          <div className="space-y-4">
            {isRescheduleMode && (
              <div className="space-y-4">
                {selectedDate && selectedTime && hasDateTimeChanged && (
                  <div className="bg-green-50 rounded-lg p-4">
                    <h4 className="font-medium text-green-900 mb-2">Rescheduled Time</h4>
                    <div className="space-y-1 text-sm text-green-700">
                      <p><strong>Date:</strong> {selectedDate.toLocaleDateString('en-US', { 
                        weekday: 'long',
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric' 
                      })}</p>
                      <p><strong>Time:</strong> {(() => {
                        // Convert 24-hour format to 12-hour format to match existing format
                        const [hours, minutes] = selectedTime.split(':');
                        const hour24 = parseInt(hours);
                        const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
                        const ampm = hour24 >= 12 ? 'PM' : 'AM';
                        return `${hour12.toString().padStart(2, '0')}:${minutes} ${ampm}`;
                      })()} ({existingBooking?.timezone || ''})</p>
                    </div>
                    
                    <div className="mt-3">
                      <Textarea
                        placeholder="Reason for rescheduling (optional)"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {isCancelMode && (
              <div className="space-y-3">
                <label htmlFor="cancellation-reason" className="block text-sm font-medium text-gray-700">
                  Reason for cancellation (optional)
                </label>
                <Textarea
                  id="cancellation-reason"
                  placeholder="Let the host know why you're cancelling..."
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              </div>
            )}

            {/* Show action buttons */}
            <div className="pt-4">
              {isRescheduleMode && selectedDate && selectedTime && hasDateTimeChanged ? (
                // Reschedule mode with new date/time selected
                <div className="space-y-3">
                  {/* Change Date & Time button above */}
                  <Button 
                    onClick={onBackToDateSelection}
                    variant="outline"
                    disabled={loading}
                    className="w-full"
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Change Date & Time  
                  </Button>
                  
                  {/* Confirm and Cancel side by side */}
                  <div className="flex gap-3">
                    <Button 
                      onClick={handleReschedule} 
                      disabled={loading}
                      className="flex-1"
                    >
                      {loading ? "Rescheduling..." : "Confirm Reschedule"}
                    </Button>
                    <Button 
                      onClick={handleCancel} 
                      variant="destructive"
                      disabled={loading}
                      className="flex-1"
                    >
                      {loading ? "Cancelling..." : "Cancel Meeting"}
                    </Button>
                  </div>
                </div>
              ) : (
                // Cancel mode or reschedule mode without new date/time
                <div className="flex gap-3">
                  {isRescheduleMode && onBackToDateSelection && (
                    <Button 
                      onClick={onBackToDateSelection}
                      variant="outline"
                      disabled={loading}
                      className="flex-1"
                    >
                      <ArrowLeft className="h-4 w-4 mr-2" />
                      Back to Calendar
                    </Button>
                  )}
                  <Button 
                    onClick={handleCancel} 
                    variant={isCancelMode ? "destructive" : "outline"}
                    disabled={loading}
                    className={isRescheduleMode && onBackToDateSelection ? "flex-1" : "w-full"}
                  >
                    {loading ? "Cancelling..." : "Cancel Meeting"}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <BookingCancelledDialog 
        isOpen={showConfirmation} 
        onClose={handleConfirmationClose} 
      />

      <Dialog open={showRescheduleSuccess} onOpenChange={setShowRescheduleSuccess}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Meeting Rescheduled Successfully</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p>Your meeting has been rescheduled to the new date and time. Calendar invites have been updated.</p>
            <Button 
              onClick={handleRescheduleSuccessClose}
              className="w-full"
            >
              Return to Home
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
